import { computePosition, flip, shift } from '@floating-ui/dom';
import { Extension } from '@tiptap/core';
import { Node } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { posToDOMRect, ReactRenderer } from '@tiptap/react';
import Suggestion, { SuggestionOptions } from '@tiptap/suggestion';
import { TEMPLATE_TAG_REGEX, TEMPLATE_TAG_SPLIT_REGEX } from 'lib/common';
import { RichTextEditorTemplateTagsSuggestions } from '.';

interface RichTextEditorTemplateTagsOptions {
  availableTags: { key: string; label: string }[] | null | undefined;
  identifier: string;
}

const findTemplateTags = function (
  doc: Node,
  availableTags: RichTextEditorTemplateTagsOptions['availableTags'],
  identifier: RichTextEditorTemplateTagsOptions['identifier']
): DecorationSet {
  const decorations: Decoration[] = [];

  doc.descendants((node, position) => {
    if (!node.text) {
      return;
    }

    Array.from(node.text.matchAll(TEMPLATE_TAG_SPLIT_REGEX)).forEach(
      (match) => {
        const tag = match[0];
        const matchingTag = availableTags?.find(
          (availableTag) => availableTag.key === tag.slice(2, -2)
        );
        const isError = availableTags?.length && !matchingTag;
        const index = match.index || 0;
        const from = position + index;
        const to = from + tag.length;
        const decoration = Decoration.inline(from, to, {
          class: 'template-tag' + (isError ? ' template-tag-error' : ''),
          ...(matchingTag
            ? {
                [`data-rich-text-editor-template-tag-${identifier}`]:
                  JSON.stringify(matchingTag)
              }
            : {})
        });

        decorations.push(decoration);
      }
    );
  });

  return DecorationSet.create(doc, decorations);
};

const updatePosition = (editor, element) => {
  const virtualElement = {
    getBoundingClientRect: () =>
      posToDOMRect(
        editor.view,
        editor.state.selection.from,
        editor.state.selection.to
      )
  };

  computePosition(virtualElement, element, {
    placement: 'bottom-start',
    strategy: 'absolute',
    middleware: [shift(), flip()]
  }).then(({ x, y, strategy }) => {
    element.style.width = 'max-content';
    element.style.position = strategy;
    element.style.left = `${x}px`;
    element.style.top = `${y}px`;
  });
};

const RichTextEditorTemplateTags =
  Extension.create<RichTextEditorTemplateTagsOptions>({
    name: 'templateTagHighlighter',
    addOptions() {
      return {
        availableTags: undefined,
        identifier: undefined
      };
    },
    addProseMirrorPlugins() {
      const availableTags = this.options.availableTags;
      const identifier = this.options.identifier;

      const suggestion: SuggestionOptions = {
        allow: ({ state, range }) => {
          const $from = state.doc.resolve(range.from);
          const textContent = $from.parent.textContent;

          let allow = true;

          // check if caret is inside tag
          Array.from(textContent.matchAll(TEMPLATE_TAG_REGEX)).forEach(
            (match) => {
              const tag = match[0];
              const index = match.index || 0;
              const from = index;
              const to = from + tag.length;

              if (range.from > from && range.to <= to + 1) allow = false;
            }
          );

          return allow;
        },
        allowedPrefixes: null,
        char: '{{',
        command: ({ editor, range, props }) => {
          // increase range.to by one when the next node is of type "text"
          // and starts with a space character
          const nodeAfter = editor.view.state.selection.$to.nodeAfter;
          const overrideSpace = nodeAfter?.text?.startsWith(' ');

          if (overrideSpace) {
            range.to += 1;
          }

          editor
            .chain()
            .focus()
            .insertContentAt(range, [
              {
                type: 'text',
                text: `{{${props.key}}}`
              },
              {
                type: 'text',
                text: ' '
              }
            ])
            .run();

          // get reference to `window` object from editor element, to support cross-frame JS usage
          editor.view.dom.ownerDocument.defaultView
            ?.getSelection()
            ?.collapseToEnd();
        },
        editor: this.editor,
        items: ({ query }) => {
          return availableTags.filter((tag) =>
            tag.key.toLowerCase().startsWith(query.toLowerCase())
          );
        },
        pluginKey: new PluginKey('template-tag-suggestion'),
        render: () => {
          let component;

          return {
            onStart: (props) => {
              component = new ReactRenderer(
                RichTextEditorTemplateTagsSuggestions,
                {
                  props,
                  editor: props.editor
                }
              );

              if (!props.clientRect) {
                return;
              }

              component.element.style.position = 'absolute';

              document.body.appendChild(component.element);

              updatePosition(props.editor, component.element);
            },

            onUpdate(props) {
              component.updateProps(props);

              if (!props.clientRect) {
                return;
              }

              updatePosition(props.editor, component.element);
            },

            onKeyDown(props) {
              if (props.event.key === 'Escape') {
                component.destroy();

                return true;
              }

              return component.ref?.onKeyDown(props);
            },

            onExit() {
              component.element.remove();
              component.destroy();
            }
          };
        }
      };

      return [
        new Plugin({
          state: {
            init(_, { doc }) {
              return findTemplateTags(doc as Node, availableTags, identifier);
            },
            apply(transaction, oldState) {
              return transaction.docChanged
                ? findTemplateTags(
                    transaction.doc as Node,
                    availableTags,
                    identifier
                  )
                : oldState;
            }
          },
          props: {
            decorations(state) {
              return this.getState(state);
            }
          }
        }),
        Suggestion(suggestion)
      ];
    }
  });

export default RichTextEditorTemplateTags;
